/**
 * GhostNodesPanel - Panel showing suggested ghost nodes from gap analysis
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Check, ChevronRight } from 'lucide-react';
import { useMapStore } from '../../stores/mapStore';

const GhostNodesPanel = () => {
  const { ghostNodes, approveGhostNode, dismissGhostNode } = useMapStore();

  if (ghostNodes.length === 0) return null;

  return (
    <motion.div
      initial={{ x: -20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      className="absolute left-4 top-1/2 -translate-y-1/2 z-30"
    >
      <div className="w-72 bg-gray-900/95 backdrop-blur border border-purple-500/30 rounded-xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 bg-purple-500/10 border-b border-purple-500/20">
          <Sparkles size={16} className="text-purple-400" />
          <h3 className="text-sm font-medium text-purple-300">
            Suggested Nodes
          </h3>
          <span className="ml-auto text-xs text-purple-400/60">
            {ghostNodes.length} gaps found
          </span>
        </div>

        {/* Ghost nodes list */}
        <div className="max-h-80 overflow-y-auto">
          <AnimatePresence>
            {ghostNodes.map((ghost, index) => (
              <motion.div
                key={ghost.title}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="border-b border-gray-800 last:border-0"
              >
                <div className="p-3">
                  <div className="flex items-start gap-2 mb-2">
                    <span
                      className={`px-1.5 py-0.5 text-[10px] rounded ${
                        ghost.priority === 'high'
                          ? 'bg-red-500/20 text-red-300'
                          : ghost.priority === 'medium'
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : 'bg-gray-700 text-gray-400'
                      }`}
                    >
                      {ghost.priority}
                    </span>
                    <span className="text-xs text-gray-500">
                      {ghost.suggestedCluster}
                    </span>
                  </div>

                  <h4 className="text-sm font-medium text-white mb-1">
                    {ghost.title}
                  </h4>
                  <p className="text-xs text-gray-400 mb-2">
                    {ghost.content?.substring(0, 80)}...
                  </p>
                  <p className="text-xs text-purple-400/80 italic mb-3">
                    {ghost.rationale}
                  </p>

                  {/* Connections preview */}
                  {ghost.wouldConnectTo?.length > 0 && (
                    <div className="flex items-center gap-1 mb-3 text-[10px] text-gray-500">
                      <ChevronRight size={10} />
                      <span>Would connect to: {ghost.wouldConnectTo.slice(0, 2).join(', ')}</span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => approveGhostNode(ghost)}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-green-500/20 text-green-300 rounded text-xs hover:bg-green-500/30 transition-colors"
                    >
                      <Check size={12} />
                      <span>Add</span>
                    </button>
                    <button
                      onClick={() => dismissGhostNode(ghost)}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-gray-700 text-gray-400 rounded text-xs hover:bg-gray-650 transition-colors"
                    >
                      <X size={12} />
                      <span>Skip</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default GhostNodesPanel;
